import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	accountActions,
	entityActions,
  entityBrowserActions,
	organizationActions,
	scenarioActions,
	periodsActions,
	currencyActions,
	allocationActions,
	businessActions
} from '../../redux/actions/';
import { Layout, Tabs, Typography, Button, Popover, Dropdown, Space , Tooltip,
		 Affix, Row, Col, message, Modal,
} from 'antd';
import { BgColorsOutlined, DownOutlined, SwapOutlined, EyeOutlined, EyeInvisibleOutlined,ApartmentOutlined, BranchesOutlined, FileTextOutlined, LoadingOutlined, CalculatorOutlined } from '@ant-design/icons';
import EntitiesBrowser from '../../components/EntitiesBrowser';
import ScenarioTable from '../../components/ScenarioTable';
import EntityBusinessCases from '../../components/EntityBusinessCases';
import OverlayedLoader from '../../components/OverlayedLoader';
import ExportBusinessCaseButton from '../../components/ExportBusinessCaseButton';
import RenderUpdater from '../../components/RenderUpdater';
import UndoButton from '../../components/UndoButton';
import ScenarioCreationModal from './ScenarioCreationModal';
import AllocationTargetError from '../../components/AllocationTargetError';
import Localization from '../../utils/localization';
import { buildTargetAccountSetForScenario } from '../../utils/allocations/allocationHelpers';
import './styles.scss';
import BusinessCaseDropdown from '../../components/BusinessCaseDropDown';
import { assembleBaseline, assemblePreview } from '../../redux/actions/assemblyActions';
import { selectShowPreview, selectActiveResult, setShowPreview, selectAssemblyCompiling, findAssemblyBucketForBusinessCase   } from '../../redux/slices/assemblySlice' 
import { toMap, asScenarioWithMaps, findEntityOrScenario, filterEntitiesByScenarioIds } from '../../utils/scenarioTransforms';
import { useColumnWidths } from '../../hooks/useColumnWidths';
import { useAccountModals } from '../../hooks/useAccountModals';
import { useAccountLocator } from '../../hooks/useAccountLocator';
import AccountSearch from '../../components/AccountSearch';
import { useMeasuredHeight } from '../../hooks/useMeasuedHeight';
import { buildCalculationAccountStructure } from '../../utils/accountStructure/accountStructureUtils';
import { customTabKey, getCustomTabs } from '../../utils/accountStructure/customTabs';


const { Sider, Content } = Layout;
const { TabPane } = Tabs;
const { Title } = Typography;

const DEFAULT_WIDTHS = { account_number: 90, name: 150 };
const CHROME_HEIGHT = 260; // BC bar + currency row + tabs + toolbar
const BS_TABLE_HEIGHT = 280; // Height for balance sheet tables
const TOOLBAR_TOP = 49; // height of the Affix'd BC bar



const EntitesAndScenario = () => {

  const [columnWidths, setColumnWidth] = useColumnWidths(
    'scenarioTable.columnWidths', 
    DEFAULT_WIDTHS
  );
	const dispatch = useDispatch();

	const entities = useSelector((state) => state.entities.entities);
  const entitiesVersion = useSelector((state) => state.entities.version ?? 0);
	const accountStructure = useSelector((state) => state.accounts.accountStructure);
  const calculationAccountStructure = useMemo(
    () =>
      buildCalculationAccountStructure(
        accountStructure,
      ),
    [accountStructure],
  );
	const currencies = useSelector((state) => state.currency.currency);
	const organization = useSelector((state) => state.organization.organization);
	const periods = useSelector((state) => state.periods.periods);
	const expandedPeriods = useSelector((state) => state.periods.expandedPeriods);
	const currentScenario = useSelector((state) => state.scenarios.currentScenario);
	const accountsNotExisting = useSelector((state) => state.scenarios.accountsNotExisting);
	const accountsCircular = useSelector((state) => state.scenarios.accountsCircular);
	const allocations = useSelector((state) => state.allocations.allocations);

	const fetchingAllocations = useSelector((state) => state.entities.fetchingAllocations);
	const fetchingEntities = useSelector((state) => state.entities.fetchingEntities);
	const fetchingStructure = useSelector((state) => state.accounts.fetchingStructure);
	const fetchingExpandedPeriods = useSelector((state) => state.periods.fetchingExpandedPeriods);
	const fetchingOrganization = useSelector((state) => state.organization.fetchingOrganization);
	const creatingEntity = useSelector((state) => state.entities.creatingEntity);
	const updatingEntity = useSelector((state) => state.entities.updatingEntity);
	const deletingEntity = useSelector((state) => state.entities.deletingEntity);
	const gettingScenario = useSelector((state) => state.scenarios.gettingScenario);
	const compilingScenario = useSelector((state) => state.scenarios.compilingScenario);
	const updatingScenarioValue = useSelector((state) => state.scenarios.updatingScenarioValue);
	const updatingOrganization = useSelector((state) => state.organization.updatingOrganization);
	const businessCases = useSelector((s) => s.business.businessCases);
	const activeAssembly = useSelector(selectActiveResult);
	const assemblyCompiling = useSelector(selectAssemblyCompiling);
  const revision = useSelector((state) => state.scenarios.revision);
  const assembly = useSelector((state) => state.assembly || { byKey: {}, currentKey: null });

  const insertingAccount = useSelector((state) => state.accounts.insertingAccount);
  const creatingSubAccount = useSelector((state) => state.accounts.creatingSubAccount);
  const deletingAccount = useSelector((state) => state.accounts.deletingAccount);
  const deletingCustomTabAccount = useSelector((state) => state.accounts.deletingCustomTabAccount);
  
  const selectedBusinessCaseId = useSelector((state) => state.business.selectedBusinessCaseId);

  const openingScenario = useSelector((state) => state.scenarios.openingScenario);
  const scenarioOpenStatus = useSelector((state) => state.scenarios.scenarioOpenStatus);

	const [currentSelection, setCurrentSelection] = useState(null);
	const [editedCells, setEditedCells] = useState([]);
	const [highlightedScenarios, setHighlightedScenarios] = useState([]);
	const [lastClickedEntity, setLastClickedEntity] = useState({});
	const [isFromScenario, setIsFromScenario] = useState(null);
  const [cellHasColor, setCellHasColor] = useState(false);
  const [scenarioCreationModalOpened, setScenarioCreationModalOpened] = useState(false);
	const [currentBusinessCaseName, setCurrentBusinessCaseName] = useState('Select business case');
	const showPreview = useSelector(selectShowPreview);
	const [forceBCView, setForceBCView] = useState(false);
  const [scenarioEntityId, setScenarioEntityId] = useState(null);
 
  const bsAssetsRef = useRef(null);
  const [activeKey, setActiveKey] = useState("1");

  const [tableHeight, setTableHeight] = useState(
    () => Math.max(240, window.innerHeight - CHROME_HEIGHT)
  );

  const [tabBodyRef, tabBodyHeight] = useMeasuredHeight(240);
  const bsSectionHeight = Math.max(200, Math.round(tabBodyHeight * 0.55));

  useEffect(() => {
    const onResize = () =>
      setTableHeight(Math.max(240, window.innerHeight - CHROME_HEIGHT));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const scenarioLoading =
  openingScenario ||
  scenarioOpenStatus === 'loading' ||
  scenarioOpenStatus === 'converging';
  
  const scenarioReady =
  scenarioOpenStatus === 'ready' &&
  !!currentScenario?.compiled;

  const viewCurrencyId = useSelector(state => state.scenarios.viewCurrencyId);
  
  const effectiveViewCurrencyId = viewCurrencyId ?? currentScenario?.scenario_currency;
  const lastConvergeRevision = useSelector(state => state.scenarios.lastConvergeRevision);

  const [displayValues, setDisplayValues] = useState(null);

  useEffect(() => {
    if (currentScenario?.compiled && currentScenario?.values) {
        setDisplayValues(currentScenario.values);
    }
}, [lastConvergeRevision]);
  
  const selectedBusinessCase = useMemo(() => {
  if (!selectedBusinessCaseId || !Array.isArray(businessCases)) return null;
  return (
    businessCases.find(
      (b) => Number(b.business_case_id) === Number(selectedBusinessCaseId)
    ) ?? null
  );
}, [selectedBusinessCaseId, businessCases]);

const editedCellIds = useMemo(
  () => new Set(editedCells.map(c => c.value_id)),
  [editedCells]
);

const allowedScenarioIds = useMemo(() => {
  if (!selectedBusinessCase) return null;
  return new Set(
    (selectedBusinessCase.business_scenario || []).map((s) => Number(s.scenario_id))
  );
}, [selectedBusinessCase]);

const bcAllocations = useMemo(() => {
  if (!selectedBusinessCase || !Array.isArray(allocations)) return [];
  const bcId = Number(selectedBusinessCase.business_case_id);
  return allocations.filter(
    (a) => Number(a.businessCaseId ?? a.business_case_id) === bcId
  );
}, [selectedBusinessCase, allocations]);

const bcAllocationsSignature = useMemo(
  () =>
    bcAllocations
      .map((a) =>
        [
          a.allocation_id,
          a.entityId,
          a.accountId,
          (a.scenarioId ?? []).join('.'),
          a.targetEntityId,
          a.targetAccountId,
          (a.targetScenarioId ?? []).join('.'),
        ].join(':')
      )
      .sort()
      .join('|'),
  [bcAllocations]
);

const hasAllocForSelected = !!selectedBusinessCase && bcAllocations.length > 0;

// normalize entities to an array (EntitiesBrowser expects an array)
			const entitiesArray = useMemo(
				() => (Array.isArray(entities) ? entities : Object.values(entities || {})),
				[entities]
			);
			
			// Memoized filtered tree
			const filteredEntities = useMemo(
				() => filterEntitiesByScenarioIds(entitiesArray, allowedScenarioIds),
				[entitiesArray, allowedScenarioIds]
			);


	const targetAccountSet = useMemo(() => {
		return buildTargetAccountSetForScenario(
			allocations,
    		currentScenario?.scenario_id
		);
	}, [allocations, currentScenario?.scenario_id]);


	// extract scenario + periods from either shape
	const activeScenario = useMemo(() => (
		activeAssembly?.scenario ?? activeAssembly?.assembledBusinessCase?.scenario ?? null
	), [activeAssembly]);

  	// Periods: use assembly periods only when showing BC total
				const bcPeriodsFromAssembly = useMemo(() => (
					activeAssembly?.periods ?? activeAssembly?.assembledBusinessCase?.periods ?? null
				), [activeAssembly]); 
	/*
	const activePeriods = useMemo(() => (
		activeAssembly?.periods ?? activeAssembly?.assembledBusinessCase?.periods ?? null
	), [activeAssembly]);
	*/	
		// parsedScenarios may live at root (baseline) or under assembledBusinessCase (preview)

		const previewMap = useMemo(() => {
			const ps =
			activeAssembly?.parsedScenarios ??
			activeAssembly?.assembledBusinessCase?.parsedScenarios ??
			[];
      
      const m = new Map();
      
      for (const s of ps) {
				const idRaw = s.scenario_id ?? s.id;
				// only set when id is usable
				if (idRaw !== undefined && idRaw !== null && !Number.isNaN(Number(idRaw))) {
					m.set(Number(idRaw), asScenarioWithMaps(s));
				}
			}
			return m;
		}, [activeAssembly]);

    //const nothingSelected = !currentSelection || (Array.isArray(currentSelection) && currentSelection.length === 0);
			const hasScenarioSelected = !!currentScenario?.scenario_id;

      const currentId = currentScenario?.scenario_id ? Number(currentScenario.scenario_id) : null;
		
		const bcScenarioForTable = useMemo(() => {
			if (!selectedBusinessCase || !activeScenario) return null;
			return asScenarioWithMaps({
				scenario_id: `bc-${selectedBusinessCase.business_case_id}`,
        name: `${selectedBusinessCase.name} (BC total)`,
				compiled: true,
				values: toMap(activeScenario.values),
				originalValues: toMap(activeScenario.originalValues ?? activeScenario.values),
				roll_up: toMap(activeScenario.roll_up),
				// keep user currency context if desired, else fall back to activeScenario currency
				scenario_currency: currentScenario?.scenario_currency ?? activeScenario.scenario_currency,
				group_currency: organization?.base_currency ?? null,
			});
			}, [selectedBusinessCase, activeScenario, currentScenario]);	
			
      const isBCView = useMemo(() => {
        // BC total is shown when:
        // - user forced BC view OR
        // - a business case is selected and no scenario is selected (so table is BC total)
        return forceBCView || (!!selectedBusinessCase && !hasScenarioSelected);
      }, [forceBCView, selectedBusinessCase, hasScenarioSelected]);
				
		const scenarioForTable = useMemo(() => {
			// If user forced BC view (e.g., after Reassemble), show BC total
  			if (forceBCView) return bcScenarioForTable ?? null;
			
			// Prefer selected scenario
  			if (hasScenarioSelected) {
				if (showPreview && currentId && previewMap.has(currentId)) {
					return previewMap.get(currentId); // compiled preview of selected scenario
					}
          if (displayValues && currentScenario) {
            return { ...currentScenario, values: displayValues };
          }
					return currentScenario;             // baseline/raw selected scenario
					}
					
					// No scenario selected → BC total
					return bcScenarioForTable ?? null;
				}, [forceBCView, hasScenarioSelected, currentId, showPreview, previewMap, currentScenario, bcScenarioForTable, displayValues]);
								
				const periodsForTable = useMemo(() => {
					const showingBC = forceBCView || (!hasScenarioSelected && !!bcScenarioForTable);
					return showingBC ? (bcPeriodsFromAssembly ?? periods) : periods;
				}, [forceBCView, hasScenarioSelected, bcScenarioForTable, bcPeriodsFromAssembly, periods]);
			
      const clearTreeHighlight = useCallback(() => {
        // Clear the blue “selected” row in the MUI tree (Redux)
        dispatch(entityBrowserActions.updateSelectedNodeIds([]));

        // Clear your extra scenario highlight list (local state)
        setHighlightedScenarios([]);

        // Optional: clear entity context too (often makes sense)
        setLastClickedEntity(null);
      }, [dispatch]);
			
			useEffect(() => {
				// At first, fetch account structure and entities
				if (!entities) dispatch(entityActions.getEntities());
        if (!accountStructure) dispatch(accountActions.getAccountStructure());
        if (!organization) dispatch(organizationActions.getOrganization());

        dispatch(periodsActions.getPeriods());
        dispatch(currencyActions.getCurrency());
        dispatch(allocationActions.getAllocations());
        dispatch(businessActions.getBusinessCases());
      }, [dispatch]);
				
				useEffect(() => {
					if (expandedPeriods == null) {
						if (!fetchingExpandedPeriods) dispatch(periodsActions.getExpandedPeriods());
					}
				}, [dispatch, fetchingExpandedPeriods, expandedPeriods]);
				
				const scenarios = useSelector((state) => state.scenarios.scenarios);
       
/*				
				useEffect(() => {
					// If we have account structure and a scenario selected that isn't compiled, compile it.
					if (
						accountStructure &&
						currentScenario &&
						expandedPeriods &&
						!currentScenario.compiled &&
						!fetchingAllocations
					) {
						
						if (!compilingScenario)
							dispatch(
						scenarioActions.calculateConvergence(
						currentScenario,
						accountStructure,
						allocations,
					),
				);
			}
		}, [
			dispatch,
			compilingScenario,
			scenarios,
			entities,
			accountStructure,
			currentScenario,
			periods,
			expandedPeriods,
			fetchingAllocations,
			allocations,
		]);
*/

// Helper to render a nice label from the currencies list
const fmtCurrency = (id, list = currencies) => {
	if (id == null) return '—';
	const items = Array.isArray(list) ? list : [];

  if (items.length === 0) return '—'; // ← currencies not loaded yet
	
	const cur = items.find(c => Number(c.id ?? c.currency_id) === Number(id));
	if (!cur) return String(id); // fallback: show the raw id

	const code   = cur.code ?? cur.currency ?? ''; // e.g. "EUR" or a name
	const symbol = cur.symbol ?? '';               // e.g. "€"
	return symbol ? `${code} (${symbol})` : (code || String(id));
};

  const currencyItems =
	(Array.isArray(currencies) ? currencies : []).map((currency) => ({
		label: currency.currency,
		key: currency.currency_id,
	}));
  
  const bcGroupCurrencyId = organization?.base_currency ?? null;

const displayedCurrencyId = isBCView
    ? bcGroupCurrencyId
    : (effectiveViewCurrencyId ?? currentScenario?.scenario_currency ?? null);

const displayedCurrencyLabel = fmtCurrency(displayedCurrencyId);
		
		const toggleColors = () => {
			setCellHasColor((prev) => !prev);
		};  
		
		const handleSetIsFromScenario = (fromScenario) => {
			setIsFromScenario(fromScenario);
			setTimeout(() => {
				setIsFromScenario(null);
			}, 10000);
		};
    const lastSubmitHadEdits = useRef(false);
		
		const handleCalculateConvergence = async () => {
      if (!calculationAccountStructure || !currentScenario || !expandedPeriods) return;
      if (compilingScenario || openingScenario) return;
      if (editedCells.length === 0) return;

      lastSubmitHadEdits.current = true;

      await dispatch(
        scenarioActions.calculateConvergence(
          currentScenario,
          calculationAccountStructure,
          allocations,
          editedCells,
          effectiveViewCurrencyId
        )
      );

      setEditedCells([]);
    };

  const handleUndone = useCallback((result) => {
    if (result.recalculated) {
      setEditedCells([]);
    } else if (result.valueIds.length > 0) {
      const undoneIds = new Set(result.valueIds);
      setEditedCells((prev) => prev.filter((c) => !undoneIds.has(c.value_id)));
    }
  }, []);

  useEffect(() => {
    dispatch(businessActions.clearSelectedBusinessCaseId());
    dispatch(setShowPreview(false));
    setForceBCView(false);
    setCurrentSelection(null);
    setHighlightedScenarios([]);
  }, [dispatch]);

  const runBusinessCaseAssembly = useCallback(
  async ({ bc, preview, force = false }) => {
    if (!bc || !periods || !calculationAccountStructure  || !expandedPeriods || !entities) return null;

    const allocsForBc = Array.isArray(allocations)
      ? allocations.filter(
          (a) => Number(a.businessCaseId ?? a.business_case_id) === Number(bc.business_case_id)
        )
      : [];

    if (preview) {
      return dispatch(
        assemblePreview({
          bc,
          periods,
          accountStructure: calculationAccountStructure,
          expandedPeriods,
          allocations: allocsForBc,
          entities,
          force,
        })
      );
    }

    return dispatch(
      assembleBaseline({
        bc,
        periods,
        accountStructure: calculationAccountStructure,
        expandedPeriods,
        allocations: [],
        entities,
        force,
      })
    );
  },
  [dispatch, periods, calculationAccountStructure, expandedPeriods, allocations, entities]
);
  

useEffect(() => {
  if (lastSubmitHadEdits.current) {
    setEditedCells([]);
    lastSubmitHadEdits.current = false;
  }
}, [revision]);

// Sync horizontal scroll across Balance Sheet tables (Assets / Liabilities / Equity)
// Sync horizontal scroll across Balance Sheet tables (Assets / Liabilities / Equity)
useEffect(() => {
  if (activeKey !== "2") return;

  let handlers = [];
  let timeoutId;

  const bind = () => {
    const getScroller = (selector) =>
      document.querySelector(`${selector} .ant-table-body`) ||
      document.querySelector(`${selector} .ant-table-content`);

    const els = [
      getScroller(".scenario-bs-assets-table"),
      getScroller(".scenario-bs-liab-table"),
      getScroller(".scenario-bs-equity-table"),
    ].filter(Boolean);

    if (els.length < 2) return;

    let isSyncing = false;

    handlers = els.map((el) => {
      const handler = () => {
        if (isSyncing) return;

        isSyncing = true;
        const left = el.scrollLeft;

        els.forEach((other) => {
          if (other !== el) {
            other.scrollLeft = left;
          }
        });

        requestAnimationFrame(() => {
          isSyncing = false;
        });
      };

      el.addEventListener("scroll", handler, { passive: true });
      return { el, handler };
    });
  };

  timeoutId = window.setTimeout(bind, 100);

  return () => {
    window.clearTimeout(timeoutId);

    handlers.forEach(({ el, handler }) => {
      el.removeEventListener("scroll", handler);
    });

    handlers = [];
  };
}, [activeKey, periodsForTable, expandedPeriods, scenarioForTable]);


const handleSelection = (selectedKey, selectedNode = null) => {
    setForceBCView(false);
    const key = selectedKey?.[0];

    if (!key && key !== 0) {
        setCurrentSelection(null);
        return;
    }

    setCellHasColor(false);

    const keyStr = String(key);
    const isScenario = keyStr.startsWith('s:');
    const isEntity   = keyStr.startsWith('e:');
    const numericId  = Number(keyStr.replace(/^[a-z]+:/, ''));

    if (!isScenario && !isEntity) return;
    if (!numericId) return;

    // Use the node passed directly from EntitiesBrowser if available
    // Avoids re-searching the raw Redux entities tree which has a different shape
    let node = selectedNode ?? null;

    if (!node) {
        // Fallback: search raw entities tree (handles any non-browser callers)
        const holder = {};
        findEntityOrScenario(keyStr, holder, entities);
        node = holder.found;
    }

    if (!node) return;

    // BC allow-list enforcement
    if (allowedScenarioIds) {
        if (isScenario && !allowedScenarioIds.has(numericId)) {
            return;
        }

        if (isEntity) {
            const hasAllowedInSubtree = (n) => {
                if (n.scenario_id) return allowedScenarioIds.has(Number(n.scenario_id));
                let ok = false;
                if (Array.isArray(n.scenario))        ok = n.scenario.some(hasAllowedInSubtree);
                if (!ok && Array.isArray(n.children)) ok = n.children.some(hasAllowedInSubtree);
                return ok;
            };
            if (!hasAllowedInSubtree(node)) return;
        }
    }

    setCurrentSelection(node);

    if (isScenario) {
        dispatch(
            scenarioActions.openScenarioAndConverge(
                node,
                calculationAccountStructure,
                allocations,
                []
            )
        );
    }
};

const handleNewEntity = (entityId = null) => {
  if (!creatingEntity) {
    // entity is null = create root
    dispatch(entityActions.createEntity(entityId ?? null));
  }
};

const handleNewScenario = (entityId) => {
  setScenarioEntityId(Number(entityId)); 
	setScenarioCreationModalOpened(true);
		};
/*
const handleNewScenarioCreation = (name, scenarioTemplateId) => {
	dispatch(entityActions.createScenario(accountStructure, currentSelection, name, scenarioTemplateId));
	setScenarioCreationModalOpened(false);
};
*/

const expandedNodeIds = useSelector(s => s.entityBrowser.expandedNodeIds || []);

 const handleNewScenarioCreation = async (name, scenarioTemplateId, localCurrencyId) => {
  try {
   const parentEntityId = scenarioEntityId;      
    if (parentEntityId == null) {
      console.warn('No scenarioEntityId set, aborting createScenario');
      return;
    }

    // 1) create and get the new scenario
    const created = await dispatch(
      scenarioActions.createScenario(
        parentEntityId,          
        name,
        scenarioTemplateId,
        localCurrencyId
      )
    );

    const newScenarioId =
      created?.scenario_id ?? created?.payload?.scenario_id ?? null;

    // 2) ensure parent is expanded
    const entityNodeId = `e:${parentEntityId}`;
    const nextExpanded = Array.from(new Set([...expandedNodeIds, entityNodeId]));
    dispatch(entityBrowserActions.updateExpandedNodeIds(nextExpanded));

    // 3) select + highlight the new scenario
    if (newScenarioId != null) {
      dispatch(
        entityBrowserActions.updateSelectedNodeIds([`s:${newScenarioId}`])
      );
      setHighlightedScenarios?.([newScenarioId]);
    }

    // 4) close modal + clear
    setScenarioCreationModalOpened(false);
    setScenarioEntityId(null);
  } catch (e) {
    console.error('Create scenario failed:', e);
  }
};

const handleReorderEntities = (parent, orderedIds) => {
  dispatch(entityActions.reorderEntities(parent, orderedIds)).catch((err) => {
    console.error('Reorder entities failed', err);
    message.error('Failed to save the new order');
  });
};

const handleReorderScenarios = (entityId, orderedIds) => {
  dispatch(scenarioActions.reorderScenarios(entityId, orderedIds)).catch((err) => {
    console.error('Reorder scenarios failed', err);
    message.error('Failed to save the new order');
  });
};

const handleNameChange = (node, newName) => {
	// scenarios
	if (node.scenario_id) {
			const scenarioUpdates = {
				name: newName,
				key: node.key,
			};

      dispatch(scenarioActions.updateScenario(node.scenario_id, scenarioUpdates));
      return;
    }
    //entities
    if (node.entity_id) {
		  if (!updatingEntity) {
        dispatch(entityActions.updateEntity(node, newName));
		}
  }
};

const getAffectedBusinessCases = (scenarioId) => {
    if (!Array.isArray(businessCases)) return [];
    return businessCases.filter((bc) =>
        (bc.business_scenario ?? []).some(
            (s) => Number(s.scenario_id) === Number(scenarioId)
        )
    );
};

// Counts the entities (excluding the node itself) and scenarios beneath a tree node.
const countEntitySubtree = (node) => {
  const result = { entities: 0, scenarioIds: [] };
  const walk = (n) => {
    (n.children || []).forEach((child) => {
      if (child.business_case_id != null) return;
      if (child.isScenario || child.scenario_id != null) {
        result.scenarioIds.push(child.scenario_id);
      } else if (child.entity_id != null) {
        result.entities += 1;
        walk(child);
      }
    });
  };
  walk(node);
  return result;
};

const confirmDeleteEntity = (node) => {
  const isGroup = node.parent == null;
  const { entities: entityCount, scenarioIds } = countEntitySubtree(node);
  const scenarioCount = scenarioIds.length;

  const affectedBusinessCases = Array.from(
    new Map(
      scenarioIds
        .flatMap((id) => getAffectedBusinessCases(id))
        .map((bc) => [bc.business_case_id, bc])
    ).values()
  );

  const count = (n, singular, pluralWord) => `${n} ${n === 1 ? singular : pluralWord}`;
  const entitiesText = count(entityCount, 'entity', 'entities');
  const subEntitiesText = count(entityCount, 'sub-entity', 'sub-entities');
  const scenariosText = count(scenarioCount, 'scenario', 'scenarios');

  const content = (
    <div>
      {isGroup ? (
        <p>
          <strong>This is a group.</strong> Deleting it deletes the entire model beneath it:{' '}
          <strong>all {entitiesText} and all {scenariosText}</strong>.
        </p>
      ) : (
        <p>
          Deleting this entity also deletes <strong>all {scenariosText}</strong> in it
          {entityCount > 0 && (
            <>
              {' '}and <strong>{subEntitiesText}</strong> (including their scenarios)
            </>
          )}
          .
        </p>
      )}

      {affectedBusinessCases.length > 0 && (
        <>
          <p>Scenarios will be removed from these business cases:</p>
          <ul>
            {affectedBusinessCases.map((bc) => (
              <li key={bc.business_case_id}>
                <strong>{bc.name}</strong>
              </li>
            ))}
          </ul>
        </>
      )}

      <p>This cannot be undone. Are you sure?</p>
    </div>
  );

  Modal.confirm({
    title: isGroup ? `Delete group "${node.name}"?` : `Delete entity "${node.name}"?`,
    content,
    okText: isGroup ? 'Delete group and everything in it' : 'Delete entity and its scenarios',
    okType: 'danger',
    cancelText: 'Cancel',
    autoFocusButton: 'cancel',
    width: 480,
    onOk: async () => {
      if (!deletingEntity) await dispatch(entityActions.deleteEntity(node));
    },
  });
};

const handleDelete = async (node) => {
  if (node !== null && node !== undefined && node.scenario_id === undefined) {
    confirmDeleteEntity(node);
  } else {
    const affected = getAffectedBusinessCases(node.scenario_id);
    const confirmContent =
      affected.length > 0 ? (
        <div>
          <p>This scenario belongs to the following business cases:</p>
          <ul>
            {affected.map((bc) => (
              <li key={bc.business_case_id}>
                <strong>{bc.name}</strong>
              </li>
            ))}
          </ul>
          <p>It will be removed from all of them. Continue?</p>
        </div>
      ) : (
        'Are you sure you want to delete this scenario?'
      );

    Modal.confirm({
      title: `Delete "${node.name}"?`,
      content: confirmContent,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await dispatch(scenarioActions.deleteScenario(node));
          if (currentScenario && node.scenario_id === currentScenario.scenario_id) {
            setCurrentSelection(null);
          }
          if (
            selectedBusinessCase &&
            allowedScenarioIds?.has(Number(node.scenario_id))
          ) {
            await runBusinessCaseAssembly({
              bc: selectedBusinessCase,
              preview: showPreview,
              force: true,
            });
          }
        } catch (err) {
          console.error(
            '[handleDelete] Scenario deletion failed, skipping re-assembly',
            err
          );
        }
      },
    });
  }
};

const handleChange = (account, value, period) => {
  if (isBCView) return;
  if (updatingScenarioValue) return;

  
  
 dispatch(scenarioActions.updateValue(account, value, period, currentScenario, effectiveViewCurrencyId));


  const accountNumber =
    account['account'] === undefined ? account.account_number : account.account.account_number;

  const valuesArr = currentScenario.values.get(accountNumber) || [];

  // must match your existing period format
  const periodId = period.slice(6);

  const currentValue = valuesArr.find(v => v.period === periodId);
  if (!currentValue) return;

   const incoming = value == null ? '' : String(value).trim();
  if (incoming === ''  || incoming === 'NaN') return;

  handleSaveEditedCell({
    value_id: currentValue.value_id,
    value: value, // "123", "#V", "#F"
  });
};


const handleSaveEditedCell = (editedCell) => {
  setEditedCells(prev => {
    const m = new Map(prev.map(c => [c.value_id, c]));
    m.set(editedCell.value_id, editedCell);
    return Array.from(m.values());
  });
};
		
const handleRollUpEffect = (scenario, record) => {
	dispatch(
		scenarioActions.updateRollUpEffect(scenario, record, calculationAccountStructure, organization),
	);
};

const handleInsertAccount = (e, account) => {
    e.stopPropagation();
    if (!insertingAccount) dispatch(accountActions.insertAccount(account));
};

const handleCreateSubAccount = (account, ftype) => {
    if (!creatingSubAccount) {
        dispatch(accountActions.createSubAccount(account, ftype));
    }
};

const handleDeleteAccount = (account) => {
    if (!deletingAccount) dispatch(accountActions.deleteAccount(account));
};

const handleDeleteCustomTabAccount = (account) => {
    if (!deletingCustomTabAccount) dispatch(accountActions.deleteCustomTabAccount(account));
};

const { focusRequest, locateAccount, clearLocate } = useAccountLocator({
    accountStructure,
    activeKey,
    setActiveKey,
});

const { callbacks: menuCallbacks, modals: accountModals } = useAccountModals({
    accountStructure,
    handleInsertAccount,
    handleCreateSubAccount,
    handleDeleteAccount,
    handleDeleteCustomTabAccount,
    onLocateAccount: locateAccount,
    // Cell formulas only exist on a single scenario, not on BC totals.
    scenario: isBCView ? null : scenarioForTable,
});

// Keep the latest menuCallbacks in a ref so stable wrappers always see fresh fns
const menuCallbacksRef = useRef(menuCallbacks);
useEffect(() => {
    menuCallbacksRef.current = menuCallbacks;
});

// Stable wrappers — these references never change
const stableMenuCallbacks = useMemo(() => ({
    onOpenAccountManagement: (...args) =>
        menuCallbacksRef.current.onOpenAccountManagement?.(...args),
    onShowWhereUsed: (...args) =>
        menuCallbacksRef.current.onShowWhereUsed?.(...args),
    // Add more stable wrappers here if other menu callbacks are passed to ScenarioTable
}), []);

const renderTableToolbar = (top = TOOLBAR_TOP) => (
    <div
        style={{
            position: 'sticky',
            top,
            zIndex: 11,
            background: '#fff',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            padding: '4px 0',
            marginBottom: '6px',
        }}
    >
        <AccountSearch
            accountStructure={accountStructure}
            value={focusRequest?.identity}
            onLocate={locateAccount}
            onClear={clearLocate}
            onShowWhereUsed={stableMenuCallbacks.onShowWhereUsed}
            style={{ marginRight: 'auto', marginLeft: '20px' }}
        />

        <Tooltip title={cellHasColor ? 'Hide input' : 'Show input'}>
            <Button
                onClick={toggleColors}
                type="primary"
                style={{
                    color: cellHasColor ? 'gray' : 'black',
                    marginRight: '40px',
                    backgroundColor: 'transparent',
                    borderColor: 'transparent',
                    boxShadow: 'none',
                    borderRadius: '0px',
                }}
            >
                <BgColorsOutlined />
            </Button>
        </Tooltip>

        <UndoButton
            onUndone={handleUndone}
            busy={compilingScenario || openingScenario}
            style={{ marginRight: '16px' }}
        />

        <Button
            onClick={handleCalculateConvergence}
            disabled={isBCView || editedCells.length === 0}
            type="primary"
            style={{
                color: editedCells.length === 0 ? '#fff' : '#1a1a1a',
                marginRight: '40px',
                backgroundColor: editedCells.length === 0 ? '#50c878' : '#d98b0d',
                borderColor: editedCells.length === 0 ? '#50c878' : '#d98b0d',
            }}
        >
            Calculate
        </Button>
    </div>
);

const createCustomTabs = (accountStructure) => {
    if (periods !== null && periods.length === 0) return null;

    return getCustomTabs(accountStructure).map((tab) => (
        <TabPane
            tab={tab.name}
            key={customTabKey(tab)}
            disabled={isFromScenario}
        >

          {renderTableToolbar()}
            <Title
                style={{
                    fontSize: '20px',
                    textAlign: 'left',
                    paddingLeft: '23px',
                    textShadow: '0.5px 0.5px 0.5px #aab7b8',
                }}
            >
                {tab.name}
                <AllocationTargetError
                    isFromScenario={isFromScenario}
                    handleSetIsFromScenario={handleSetIsFromScenario}
                />
            </Title>
            <ScenarioTable
                {...sharedScenarioTableProps}
                accounts={tab.custom_tab_accounts}
            />
        </TabPane>
    ));
};
	
const handleUpdateScenarioCurrency = (e) => {
	if (isBCView) return;
	if (!hasScenarioSelected) return;
	if (!currentScenario || !calculationAccountStructure  || !expandedPeriods) return;

	const selectedCurrencyId = Number(e.key);
    dispatch(
        scenarioActions.calculateConvergence(
            currentScenario,
            calculationAccountStructure,
            allocations,
            [],  // no edits
            selectedCurrencyId, 
        )
    );
};

const scenarioCurrencyMenuProps = {
	items: currencyItems,
	onClick: handleUpdateScenarioCurrency,
};



useEffect(() => {
  if (!selectedBusinessCase) {
    setCurrentBusinessCaseName('Select business case');
    return;
  }
  setCurrentBusinessCaseName(selectedBusinessCase.name);
}, [selectedBusinessCase]);

useEffect(() => {
  if (!selectedBusinessCase) return;
  if (!periods || !calculationAccountStructure  || !expandedPeriods || !entities) return;
  if (assemblyCompiling) return;

  const shouldUsePreview = hasAllocForSelected;

  const autoAssemble = async () => {
    try {
      dispatch(setShowPreview(shouldUsePreview));

      await runBusinessCaseAssembly({
        bc: selectedBusinessCase,
        preview: shouldUsePreview,
        force: false,
      });
    } catch (err) {
      console.error("Auto-assemble on EntitiesAndScenario page failed", err);
    }
  };

  autoAssemble();
}, [
  dispatch,
  selectedBusinessCase?.business_case_id,
  periods,
  calculationAccountStructure,
  expandedPeriods,
  entities,
  bcAllocationsSignature,
  runBusinessCaseAssembly,
]);

 const selectedBcWarmStatus = useSelector(state => state.business.bcWarmStatuses?.[selectedBusinessCase?.business_case_id] ?? 'cold');

const handleSelectBusinessCase = (bcOrNull) => {
  clearTreeHighlight();

  if (!bcOrNull) {
    dispatch(businessActions.clearSelectedBusinessCaseId());
    dispatch(setShowPreview(false));
    setForceBCView(false);
    setCurrentSelection(null);
    return;
  }

  const bc =
    typeof bcOrNull === "object"
      ? bcOrNull
      : businessCases?.find(
          (b) => Number(b.business_case_id) === Number(bcOrNull)
        );

  if (!bc) return;

  const allocsForBc = Array.isArray(allocations)
    ? allocations.filter(
        (a) =>
          Number(a.businessCaseId ?? a.business_case_id) ===
          Number(bc.business_case_id)
      )
    : [];

  const shouldUsePreview = allocsForBc.length > 0;

  dispatch(businessActions.setSelectedBusinessCaseId(Number(bc.business_case_id)));
  dispatch(setShowPreview(shouldUsePreview));

  setForceBCView(true);
  setCurrentSelection(null);
};

// Rerun the currently selected mode (useful after scenario changes)
const handleReassembleBC = async () => {
  clearTreeHighlight();
  if (!selectedBusinessCase) return;

  try {
    await runBusinessCaseAssembly({
      bc: selectedBusinessCase,
      preview: showPreview,
      force: true,
    });

    setForceBCView(true);
    setCurrentSelection(null);
  } catch (err) {
    console.error("handleReassembleBC failed", err);
    message.error(err?.message || "Failed to recalculate business case");
  }
};

function BusinessCaseOverview({ bc, allocs = [] }) {
  if (!bc) return null;
  const scenarios = Array.isArray(bc.business_scenario) ? bc.business_scenario : [];


  return (
    <div style={{ padding: 16 }}>
      <h3 style={{ marginBottom: 8 }}>{bc.name}</h3>
      <div style={{ opacity: 0.8, marginBottom: 12 }}>
        {bc.description || 'No description'}
      </div>

      <div style={{ display: 'flex', gap: 24, marginBottom: 16 }}>
        <div>
          <strong>Scenarios:</strong> {(bc.business_scenario || []).length}
        </div>
        <div>
          <strong>Allocations:</strong> {allocs.length}
        </div>
      </div>

      <div style={{ marginBottom: 8 }}>
        <strong>Scenarios in this Business Case</strong>
      </div>
      <div
        style={{
          maxHeight: 320,
          overflow: 'auto',
          border: '1px solid #eee',
          borderRadius: 8,
          padding: 12,
        }}
      >
        {(bc.business_scenario || []).map((s) => (
          <div key={s.scenario_id}>• {s.name}</div>
        ))}
      </div>
    </div>
  );
}

const disabled = !selectedBusinessCase || assemblyCompiling;

const handleClick = async () => {
  clearTreeHighlight();
  if (!selectedBusinessCase || assemblyCompiling) return;

  const next = !showPreview;

  if (next && !hasAllocForSelected) {
    message.warning("No allocations available to use.");
    return;
  }

  dispatch(setShowPreview(next));

  try {
    await runBusinessCaseAssembly({
      bc: selectedBusinessCase,
      preview: next,
      force: false,
    });
  } catch (err) {
    message.error(err?.message || "Failed to switch allocation view");
  }
};

// First — the entities walk (top level, alongside other memos)
const scenarioMetaFromEntities = useMemo(() => {
  const m = new Map();
  const walk = (nodes, currentEntityName) => {
    (nodes || []).forEach((n) => {
      const nextEntityName =
        n?.entity_id != null ? (n.name || currentEntityName) : currentEntityName;
      if (n?.scenario_id != null) {
        m.set(Number(n.scenario_id), {
          name: n.name,
          entityName: currentEntityName,
        });
      }
      if (Array.isArray(n.scenario)) walk(n.scenario, nextEntityName);
      if (Array.isArray(n.children)) walk(n.children, nextEntityName);
    });
  };
  walk(Array.isArray(entities) ? entities : Object.values(entities || {}), null);
  return m;
}, [entities]);

// Second — replaces the block you quoted, in the same spot
const businessCaseForAudit = useMemo(() => {
  if (!isBCView) return null;
  if (!selectedBusinessCase || !activeAssembly) return null;

  const sourceSc = activeAssembly.scenario;
  if (!sourceSc) return null;

  const parsed = activeAssembly.parsedScenarios;
  if (!Array.isArray(parsed) || parsed.length === 0) return null;

  const scenarios = parsed
    .filter((s) => s?.scenario_id != null)
    .map((s) => {
      const meta = scenarioMetaFromEntities.get(Number(s.scenario_id));
      return {
        ...s,
        values: toMap(s.values),
        name: s.name || meta?.name || `Scenario ${s.scenario_id}`,
        entityName: meta?.entityName ?? null,
      };
    });

  return {
    source: { ...sourceSc, values: toMap(sourceSc.values) },
    scenarios,
  };
}, [isBCView, selectedBusinessCase, activeAssembly, scenarioMetaFromEntities]);

const tableLoading =
  !isBCView &&
  (openingScenario || compilingScenario || scenarioOpenStatus === 'converging');

  const sharedScenarioTableProps = {
    loading: tableLoading,
    cellHasColor,
    structure: accountStructure,
    scenario: scenarioForTable,
    editedCells,
    editedCellIds,      
    organization,
    periods: periodsForTable,
    expandedPeriods,
    handleSaveEditedCell,
    handleChange,
    handleRollUpEffect,
    editable: !isBCView,
    scenarioCurrencyId: displayedCurrencyId,
    currencies,
    allocations,
    handleSetIsFromScenario,
    targetAccountSet,
    columnWidths,
    setColumnWidth,
    columnDefaults: DEFAULT_WIDTHS,
    isBCView,
    businessCase: businessCaseForAudit,
    tableHeight,
    focusRequest,
    ...stableMenuCallbacks,
};

const renderCurrencyIndicator = () =>  {
  const localCurrencyId = currentScenario?.scenario_currency ?? null;
  const localCurrencyLabel = fmtCurrency(localCurrencyId);

  return (  
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      marginBottom: 12,
      marginLeft: 20,
    }}
  >

    {!isBCView && (
  <>
    <div style={{ fontSize: 12, color: '#666' }}>Local currency</div>
    <Button style={{ minWidth: '150px' }} disabled>
      <Space>{localCurrencyLabel}</Space>
    </Button>

    {/* Divider */}
    <div style={{ width: 1, height: 24, background: '#d9d9d9', margin: '0 4px' }} />
  </>
)}
    <div style={{ fontSize: 12, color: '#666' }}>
      {isBCView ? 'Group currency' : 'Scenario currency'}
    </div>

    {isBCView ? (
      <Button style={{ minWidth: '150px' }} disabled>
        <Space>{displayedCurrencyLabel}</Space>
      </Button>
    ) : (
      <Dropdown menu={scenarioCurrencyMenuProps} trigger={['click']}>
        <Button style={{ minWidth: '150px' }}>
          <Space>
            {displayedCurrencyLabel}
            <DownOutlined />
          </Space>
        </Button>
      </Dropdown>
    )}
  </div>
);
};

const renderContent = () => {
  if (scenarioLoading) {
    return <OverlayedLoader />;
  } else if (updatingOrganization) {
    return <RenderUpdater />;
  } else if (assemblyCompiling) {
    return <OverlayedLoader />;
  } else if (scenarioForTable && periods) {
    return (
      <>
      {renderCurrencyIndicator()}
      <Tabs onChange={setActiveKey} activeKey={activeKey} type="card" destroyInactiveTabPane>
        <TabPane
          tab={Localization.common.profitAndLoss}
          key="1"
          disabled={isFromScenario != null}
        >
           {renderTableToolbar()}

          <AllocationTargetError
            isFromScenario={isFromScenario}
            handleSetIsFromScenario={handleSetIsFromScenario}
          />
           <div ref={tabBodyRef} style={{ flex: 1, minHeight: 0 }}>
          <ScenarioTable {...sharedScenarioTableProps} accounts={accountStructure?.profitloss} />
          </div>
        </TabPane>

        <TabPane
          tab={Localization.common.balanceSheet}
          key="2"
          disabled={isFromScenario != null}
        >
          <Popover
            placement="leftBottom"
            title="Calculation error(s)"
            open={accountsNotExisting.length > 0 || accountsCircular.length > 0}
            content={() => {
              let errorMsg = '';
              if (accountsNotExisting.length > 0) {
                errorMsg =
                  'Account(s) ' +
                  accountsNotExisting.join(', ') +
                  ' does not exist - remove the reference(s) to calculate scenario succesfully.';
              } else {
                errorMsg =
                  'Accounts ' +
                  accountsCircular.join(', ') +
                  ' are part of a circular reference - please break it to calculate scenario succesfully.';
              }
              return errorMsg;
            }}
          >
            {renderTableToolbar()}
          </Popover>

          <div
              ref={tabBodyRef}
              id="bs-scroll"
              style={{ flex: 1, minHeight: 0, overflow: 'auto' }}
            >

          <Title level={4} style={{ textAlign: 'left' }}>
            {Localization.common.assets}
            <AllocationTargetError
              isFromScenario={isFromScenario}
              handleSetIsFromScenario={handleSetIsFromScenario}
            />
          </Title>

          <div className="scenario-bs-assets-table">
            <ScenarioTable
              {...sharedScenarioTableProps}
              tableHeight={undefined}
              accounts={accountStructure?.balancesheet?.assets}
            />
          </div>

          <Title level={4} style={{ textAlign: 'left', marginTop: '40px' }}>
            {Localization.common.liabilities}
          </Title>

          <div className="scenario-bs-liab-table">
            <ScenarioTable
              {...sharedScenarioTableProps}
              tableHeight={undefined}
              accounts={accountStructure?.balancesheet?.liabilities}
            />
          </div>

          <Title level={4} style={{ textAlign: 'left', marginTop: '40px' }}>
            {Localization.common.equities}
          </Title>

          <div className="scenario-bs-equity-table">
            <ScenarioTable
              {...sharedScenarioTableProps}
              tableHeight={undefined}
              accounts={accountStructure?.balancesheet?.equities}
            />
          </div>
          </div>
        </TabPane>

        <TabPane tab={Localization.common.cashFlow} key="3" disabled={isFromScenario != null}>

           {renderTableToolbar()}

          <AllocationTargetError
            isFromScenario={isFromScenario}
            handleSetIsFromScenario={handleSetIsFromScenario}
          />
          <div ref={tabBodyRef} style={{ flex: 1, minHeight: 0 }}>
          <ScenarioTable {...sharedScenarioTableProps} accounts={accountStructure?.cashflow} />
          </div>
        </TabPane>

        <TabPane tab={Localization.common.drivers} key="4" disabled={isFromScenario != null}>

           {renderTableToolbar()}

          <AllocationTargetError
            isFromScenario={isFromScenario}
            handleSetIsFromScenario={handleSetIsFromScenario}
          />
          <div ref={tabBodyRef} style={{ flex: 1, minHeight: 0 }}>
          <ScenarioTable {...sharedScenarioTableProps} accounts={accountStructure?.drivers} />
          </div>
        </TabPane>

        {createCustomTabs(accountStructure)}
      </Tabs>
      </>
    );
  } else if (selectedBusinessCase) {
    const noSelection =
      !currentSelection || (Array.isArray(currentSelection) && currentSelection.length === 0);

    const allocsToShow = noSelection ? [] : bcAllocations ?? [];

    return <BusinessCaseOverview bc={selectedBusinessCase} allocs={allocsToShow} />;
  } else {
    // return null;
    return (
      <EntityBusinessCases
        entity={currentSelection}
        setHighlightedScenarios={setHighlightedScenarios}
        showHeader={false}
      />
    );
  }
};

return (
  <Layout className="entities-scenario-page"  style={{ height: '100vh', marginLeft: '-16px' }}>
    <ScenarioCreationModal
      open={scenarioCreationModalOpened}
      onConfirm={handleNewScenarioCreation}
      onCancel={() => {
        setScenarioCreationModalOpened(false);setScenarioEntityId(null);
      }}
    />

    <Sider
      collapsible
      collapsedWidth={0}
      trigger={
        <div style={{ background: '#192d40' }}>
          <button>
            <SwapOutlined />
          </button>
        </div>
      }
      style={{ background: '#192d40' }}
      width={300}
    >
      {entities ? (
        
        <EntitiesBrowser
          entityStructure={allowedScenarioIds ? filteredEntities : entitiesArray}
          handleSelection={handleSelection}
          handleNewScenario={handleNewScenario}
          handleNewEntity={handleNewEntity}
          handleDelete={handleDelete}
          handleNameChange={handleNameChange}
          editable
          checkedNodes={[]}
          highlightedScenarios={highlightedScenarios}
          setHighlightedScenarios={setHighlightedScenarios}
          lastClickedEntity={lastClickedEntity}
          setLastClickedEntity={setLastClickedEntity}
          entitiesVersion={entitiesVersion} 
          handleReorderEntities={handleReorderEntities}
          handleReorderScenarios={handleReorderScenarios}
           reorderable={!allowedScenarioIds}
        />
      ) : null}

      {(fetchingEntities ||
        fetchingStructure ||
        fetchingOrganization ||
        creatingEntity ||
        deletingEntity ||
        updatingEntity) && <OverlayedLoader />}
    </Sider>

    <Content style={{ position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', }}>
      {/* Always-visible BC bar */}
      <Affix offsetTop={0}>
        <div style={{ background: '#fff', borderBottom: '1px solid #eee', padding: '8px 12px', zIndex: 9 }}>
          <Row align="middle" justify="space-between" gutter={12} wrap={false}>
            <Col>
              <BusinessCaseDropdown
                handleSelectBusinessCase={handleSelectBusinessCase}
                showEdit={false}
                showDelete={false}
                currentBusinessCaseName={currentBusinessCaseName}
                allowNone
                noneLabel="None (no business case)"
                selectedBusinessCaseId={selectedBusinessCase?.business_case_id ?? null}
                bordered={false}
              />

              {selectedBcWarmStatus === 'warming' && (
                <Tooltip title="Business case is being updated in the background">
                    <span style={{ 
                        fontSize: 12, 
                        color: '#fa8c16',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                    }}>
                        <LoadingOutlined spin />
                        Updating...
                    </span>
                </Tooltip>
            )}

              <Tooltip title="Run Businesscase">
                <CalculatorOutlined
                  style={{ marginLeft: 8, fontSize: 24 }}
                  onClick={handleReassembleBC}
                  disabled={!selectedBusinessCase || assemblyCompiling}
                  loading={assemblyCompiling}
                >
                  RunBC
                </CalculatorOutlined>
              </Tooltip>
            </Col>

            <Col style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {showPreview && !hasAllocForSelected ? (
                <Tooltip title="No allocations available to use">
                  <span>
                    <Button
                      shape="circle"
                      onClick={handleClick}
                      disabled={disabled}
                      loading={assemblyCompiling}
                      aria-pressed={showPreview}
                      icon={showPreview ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                    />
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title={showPreview ? 'With BC allocations' : 'Without BC allocations'}>
                  {disabled ? (
                    <span>
                      <Button
                        shape="circle"
                        onClick={handleClick}
                        disabled={disabled}
                        loading={assemblyCompiling}
                        aria-pressed={showPreview}
                        icon={showPreview ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                      />
                    </span>
                  ) : (
                    <Button
                      shape="circle"
                      onClick={handleClick}
                      disabled={disabled}
                      loading={assemblyCompiling}
                      aria-pressed={showPreview}
                      icon={showPreview ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                    />
                  )}
                </Tooltip>
              )}

              {selectedBusinessCase && (
                <ExportBusinessCaseButton
                  businessCase={selectedBusinessCase}
                  disabled={assemblyCompiling}
                />
              )}
            </Col>
          </Row>
        </div>
      </Affix>

      {/* existing loaders + content */}
      {assemblyCompiling && <OverlayedLoader />}
      {renderContent()}
    </Content>
    {accountModals}
  </Layout>
);

};

export default EntitesAndScenario;
